
import { Event, EventType, EventAudience } from './types';
import { regions, eventTypeLabels } from './eventLabels';
import { createDate, generateId } from './eventHelpers';
import { loadProfessions } from '@/services/professionService';
import { EventStatus } from '@/services/eventService';
import { Profession } from '@/lib/professionTypes';

// Event titles by type mapping for the generator
const titlesByType: Record<EventType, string[]> = {
  conference: [
    'Orvosi Innovációk Konferencia',
    'Egészségügyi Technológiák Konferencia',
    'Kardiológiai Kongresszus',
    'Belgyógyászati Társaság Ülése',
    'Farmakológiai Konferencia',
    'Orvosi Genetika Konferencia',
    'Nemzetközi Onkológiai Szimpózium',
    'Sürgősségi Ellátás Konferencia',
    'Közegészségügyi Tanácskozás'
  ],
  workshop: [
    'Sebészeti Technikák Workshop',
    'Diagnosztikai Eljárások Workshop',
    'Betegvezetés Workshop',
    'Terápiás Eljárások Workshop',
    'Minimálisan Invazív Beavatkozások Workshop',
    'Digitális Képalkotás Workshop',
    'Klinikai Gyakorlatok Workshop'
  ],
  training: [
    'Szakorvosi Továbbképzés',
    'Ápolói Oktatás',
    'Gyógyszerészeti Képzés',
    'Sürgősségi Ellátás Tréning',
    'Kommunikációs Készségfejlesztés',
    'Klinikai Vizsgálatok Képzés',
    'Orvosi Adminisztráció Tréning'
  ],
  webinar: [
    'Online Orvosszakmai Webinárium',
    'Egészségügyi Informatika Webinárium',
    'Telemedicina Webinárium',
    'Digitális Patológia Webinárium',
    'Gyógyszerinnovációk Webinárium',
    'Egészségügyi Menedzsment Webinárium'
  ],
  seminar: [
    'Gyermekgyógyászati Szeminárium',
    'Neurológiai Szeminárium',
    'Aneszteziológiai Szeminárium',
    'Pszichiátriai Szeminárium',
    'Bőrgyógyászati Szeminárium',
    'Belgyógyászati Szeminárium'
  ],
  networking: [
    'Egészségügyi Vezetők Találkozója',
    'Fiatal Orvosok Fóruma',
    'Gyógyszeripari Kapcsolatépítés',
    'Egészségügyi Startupok Találkozója',
    'Nemzetközi Orvosi Kapcsolatok Fóruma',
    'Egészségügyi Szakemberek Estje'
  ],
  exhibition: [
    'Orvosi Műszerek Kiállítása',
    'Gyógyszeripari Innovációk Bemutatója',
    'Egészségügyi Technológiák Kiállítása',
    'Rehabilitációs Eszközök Kiállítása',
    'Digital Health Expo',
    'Gyógyászati Segédeszközök Kiállítása'
  ],
  meetup: [
    'Orvosi Meetup',
    'Egészségügyi Szakemberek Találkozója',
    'Gyógyszerészeti Meetup',
    'Innovációs Meetup az Egészségügyben',
    'Kutatói Találkozó',
    'Szakmai Közösségi Összejövetel'
  ],
  congress: [
    'Magyar Orvosi Kongresszus',
    'Nemzetközi Egészségügyi Kongresszus',
    'Szakmai Kongresszus',
    'Tudományos Kongresszus',
    'Orvostudományi Kongresszus',
    'Interdiszciplináris Kongresszus'
  ],
  course: [
    'Orvosi Képzés',
    'Szakmai Tanfolyam',
    'Orvosi Továbbképző Tanfolyam',
    'Készségfejlesztő Tanfolyam',
    'Klinikai Gyakorlati Tanfolyam',
    'Specializációs Képzés'
  ],
  symposium: [
    'Orvostudományi Szimpózium',
    'Kutatási Eredmények Szimpóziuma',
    'Interdiszciplináris Szimpózium',
    'Szakmai Találkozó',
    'Tudományos Szimpózium',
    'Nemzetközi Szimpózium'
  ]
};

// Cache for loaded professions
let professionsCache: Profession[] | null = null;

// Helper function to get profession IDs
const loadProfessionIds = async (): Promise<string[]> => {
  if (!professionsCache) {
    try {
      const professions = await loadProfessions();
      professionsCache = professions;
    } catch (error) {
      console.error('Error loading professions:', error);
      return [];
    }
  }

  return professionsCache.map(p => p.id);
};

// Generate hundreds of events from February to June 2025
export const generateFutureEvents = async (): Promise<Event[]> => {
  const events: Event[] = [];
  const eventTypes: EventType[] = ['conference', 'workshop', 'training', 'webinar', 'seminar', 'networking', 'exhibition'];

  // Base audiences - will be supplemented with professions
  const baseAudiences: EventAudience[] = ['doctors', 'nurses', 'medical_students', 'pharmacists', 'healthcare_professionals', 'general_public'];

  // Load profession IDs to use as additional audience types
  const professionIds = await loadProfessionIds();
  const allPossibleAudiences = [...baseAudiences, ...professionIds as EventAudience[]];

  // For each month from February to June 2025
  for (let month = 2; month <= 6; month++) {
    // Determine the number of days in the month
    const daysInMonth = new Date(2025, month, 0).getDate();

    // Generate 80-120 events per month
    const eventsCount = Math.floor(Math.random() * 41) + 80; // Random number between 80-120

    for (let i = 0; i < eventsCount; i++) {
      const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];

      // Random audience (1-3 types)
      const audienceCount = Math.floor(Math.random() * 3) + 1;
      const eventAudiences: EventAudience[] = [];

      // First include 1-2 base audiences
      const baseAudienceCount = Math.min(Math.floor(Math.random() * 2) + 1, audienceCount);
      for (let j = 0; j < baseAudienceCount; j++) {
        const audience = baseAudiences[Math.floor(Math.random() * baseAudiences.length)];
        if (!eventAudiences.includes(audience)) {
          eventAudiences.push(audience);
        }
      }

      // Then include some profession IDs if we have space and available professions
      if (professionIds.length > 0) {
        const remainingSlots = audienceCount - eventAudiences.length;
        for (let j = 0; j < remainingSlots; j++) {
          const professionId = professionIds[Math.floor(Math.random() * professionIds.length)] as EventAudience;
          if (!eventAudiences.includes(professionId)) {
            eventAudiences.push(professionId);
          }
        }
      }

      // Random region
      const region = regions[Math.floor(Math.random() * regions.length)];

      // 20% chance of being online
      const online = Math.random() < 0.2;

      // Random start day
      const startDay = Math.floor(Math.random() * daysInMonth) + 1;

      // Random duration (1-5 days for conferences, 1-2 for others)
      let eventDuration = 1;
      if (eventType === 'conference') {
        eventDuration = Math.floor(Math.random() * 5) + 1;
      } else if (eventType !== 'webinar' && eventType !== 'seminar') {
        eventDuration = Math.floor(Math.random() * 2) + 1;
      }

      // Ensure we don't exceed the month
      const adjustedDuration = Math.min(eventDuration, daysInMonth - startDay + 1);

      // Random start hour (8-18)
      const startHour = Math.floor(Math.random() * 9) + 8; // 8 AM to 4 PM

      // Random event title from the type-specific list
      const titleOptions = titlesByType[eventType];
      const baseTitle = titleOptions[Math.floor(Math.random() * titleOptions.length)];

      // Add some randomization to titles
      const titleSuffix = Math.random() < 0.3 ? ` ${Math.floor(Math.random() * 2025)}` : '';
      const titlePrefix = Math.random() < 0.3 ? `${region}i ` : '';

      events.push({
        id: generateId(),
        name: `${titlePrefix}${baseTitle}${titleSuffix}`,
        description: `${eventTypeLabels[eventType]} az egészségügyi szakemberek számára. Részletes információkért látogasson el honlapunkra.`,
        dateStart: createDate(2025, month, startDay, startHour, 0),
        dateEnd: createDate(2025, month, startDay + adjustedDuration - 1, startHour + 4 + Math.floor(Math.random() * 4), 0),
        timeNotSpecified: false,
        venue: online ? {
          city: 'Online',
          name: 'Zoom/Teams webinárium',
          address: 'Online esemény',
          online: true
        } : {
          city: region,
          name: `${region}i ${Math.random() < 0.5 ? 'Konferencia Központ' : 'Orvostudományi Egyetem'}`,
          address: `${['Fő', 'Petőfi', 'Kossuth', 'Széchenyi', 'Arany János'][Math.floor(Math.random() * 5)]} ${Math.random() < 0.5 ? 'utca' : 'tér'} ${Math.floor(Math.random() * 100) + 1}.`,
          region: region
        },
        type: eventType,
        organizer: {
          name: `Magyar ${eventTypeLabels[eventType]} ${['Társaság', 'Egyesület', 'Szövetség', 'Alapítvány'][Math.floor(Math.random() * 4)]}`,
          contactEmail: `info@${eventType.toLowerCase().substring(0, 3)}${Math.floor(Math.random() * 1000)}.hu`,
          website: `https://${eventType.toLowerCase().substring(0, 3)}${Math.floor(Math.random() * 1000)}.hu`
        },
        registrationUrl: `https://reg-${eventType.toLowerCase().substring(0, 3)}${Math.floor(Math.random() * 1000)}.hu/register`,
        isFeatured: Math.random() < 0.15, // 15% featured
        imageUrl: Math.random() < 0.85 ? `https://source.unsplash.com/random/?medical,${eventType}` : undefined, // 85% have images
        tags: ['egészségügy', eventType, ...['kutatás', 'gyakorlat', 'innováció', 'technológia', 'terápia', 'diagnosztika'].slice(0, Math.floor(Math.random() * 3) + 1)],
        status: Math.random() < 0.95 ? EventStatus.PUBLISHED : (Math.random() < 0.5 ? EventStatus.CANCELLED : EventStatus.POSTPONED), // 95% scheduled, rest are cancelled or postponed
        creditPoints: Math.random() < 0.7 ? Math.floor(Math.random() * 20) + 1 : undefined, // 70% chance of having credit points
        professions: eventAudiences.filter(a => professionIds.includes(a)) // Store profession IDs separately for reference
      });
    }
  }

  return events;
};
